class KeenSliderWrapper {
	constructor(element) {
		this.element = element;
		this.options = this.getOptions();

		this.boundsCheckTimeout = null;
		this.navigationWrapper = null;
		this.progressNavManager = null;
		this.resizeObserver = null;
		this.slider = null;
		this.durationCache = new Map(); // Cache for slide durations

		this.init();
	}

	/**
	 * Build options from data attributes with sensible defaults.
	 */
	getOptions() {
		const { dataset } = this.element;

		const getBool = (key, def) =>
			dataset[key] !== undefined ? dataset[key] === 'true' : def;
		const getInt = (key, def) => {
			if (dataset[key] === undefined) return def;
			const number = parseInt(dataset[key], 10);
			return isNaN(number) ? def : number;
		};

		const autoplay = getBool('autoplay', false);
		const pauseOnHover = getBool('pauseOnHover', false);
		const loop = getBool('loop', true);
		const drag = getBool('drag', true);
		const transition = dataset.transition === 'fade' ? 'fade' : 'carousel';
		const mobileSpacing = getInt('spacingMobile', 16);
		const desktopSpacing = getInt('spacingDesktop', 16);

		const isFadeTransition = transition === 'fade';

		return {
			autoplay: {
				distance: 1,
				duration: 6.25, // seconds
				enabled: autoplay,
				pauseOnHover,
			},
			breakpoints: {
				'(min-width: 870px)': {
					slides: {
						origin: isFadeTransition ? 'center' : 'auto',
						perView: isFadeTransition ? 1 : 'auto',
						spacing: isFadeTransition ? 0 : desktopSpacing,
					},
				},
			},
			drag,
			initial: 0,
			loop,
			mode: isFadeTransition ? 'snap' : 'free-snap',
			renderMode: isFadeTransition ? 'custom' : 'performance',
			transition,
			slides: {
				origin: isFadeTransition ? 'center' : 'auto',
				perView: isFadeTransition ? 1 : 'auto',
				spacing: isFadeTransition ? 0 : mobileSpacing,
			},
		};
	}

	init() {
		if (!this.element) return;

		this.initAutoplay();
		this.initSlider();
	}

	/**
	 * Prepare autoplay configuration and hover wiring.
	 */
	initAutoplay() {
		this.autoplayOptions = this.options.autoplay;
		this.autoplayEnabled = this.autoplayOptions.enabled;
		this.isHovered = false;
		this.pauseOnHover = this.autoplayOptions.pauseOnHover;
		this.autoplayTimer = null;
		this.onVisibilityChange = () => {
			if (document.hidden) {
				this.stopAutoplay();
			} else if (this.slider) {
				this.startAutoplay(this.slider);
			}
		};

		// Respect prefers-reduced-motion
		try {
			if (
				window.matchMedia &&
				window.matchMedia('(prefers-reduced-motion: reduce)').matches
			) {
				this.autoplayEnabled = false;
				this.element.setAttribute('data-reduced-motion', 'true');
			}
		} catch (error) {
			// eslint-disable-next-line no-console
			console.error('KeenSlider reduced motion detection failed:', error);
		}

		this.animation = {
			duration: Math.max(0, this.autoplayOptions.duration * 1000),
			easing: (t) => t,
		};

		this.hoverEvents();
	}

	/**
	 * Initialize KeenSlider if available and register lifecycle hooks.
	 */
	initSlider() {
		if (typeof KeenSlider !== 'function') return;

		// eslint-disable-next-line no-undef
		this.slider = new KeenSlider(this.element, {
			breakpoints: this.options.breakpoints,
			drag: this.options.drag,
			loop: this.options.loop,
			mode: this.options.mode,
			renderMode: this.options.renderMode,
			slides: this.options.slides,
			// Lifecycle hooks
			created: (slider) => {
				this.slider = slider;
				document.addEventListener('visibilitychange', this.onVisibilityChange);
				this.bindFocusPause();
				this.updateA11y(slider);
				this.updateFadeTransition(slider);

				// Only precalculate durations for autoplay-enabled sliders
				if (this.autoplayEnabled) {
					this.precalculateSlideDurations();
				}

				this.initNavigation();
				this.startAutoplay(slider);
			},
			slideChanged: (slider) => {
				this.updateA11y(slider);
				this.updateNavigationState(slider);
				this.updateProgressNavigation(slider.track.details.rel);
				this.updateFadeTransition(slider);
			},
			detailsChanged: (slider) => {
				this.updateFadeTransition(slider);
			},
			dragStarted: () => this.stopAutoplay(),
			dragEnded: (slider) => this.startAutoplay(slider),
			destroyed: () => {
				document.removeEventListener(
					'visibilitychange',
					this.onVisibilityChange,
				);
				this.stopAutoplay();
				this.destroyNavigation();

				if (this.boundsCheckTimeout) {
					clearTimeout(this.boundsCheckTimeout);
					this.boundsCheckTimeout = null;
				}
			},
		});
	}

	/**
	 * Initialize navigation if enabled
	 */
	initNavigation() {
		const navigationEnabled = this.element.dataset.navigation === 'true';
		if (!this.slider) return;

		this.parentContainer = this.element.parentElement;
		if (!this.parentContainer) return;

		this.navigationWrapper = this.parentContainer.querySelector(
			'.grow-slider__navigation-wrapper',
		);
		this.updateNavigationVisibility();
		this.initResizeObserver();

		this.initProgressNavigation();

		if (!navigationEnabled) return;

		const buttons = this.parentContainer.querySelectorAll(
			'.grow-slider__navigation-button--prev, .grow-slider__navigation-button--next',
		);
		this.prevButton = buttons[0] || null;
		this.nextButton = buttons[1] || null;

		// Bind event handlers once to avoid repeated .bind() calls
		this.boundHandlePrevClick = this.handlePrevClick.bind(this);
		this.boundHandleNextClick = this.handleNextClick.bind(this);
		this.boundHandleKeyDown = this.handleKeyDown.bind(this);
		this.boundHandleSliderKeyDown = this.handleSliderKeyDown.bind(this);

		if (this.prevButton) {
			this.prevButton.addEventListener('click', this.boundHandlePrevClick);
			this.prevButton.addEventListener('keydown', this.boundHandleKeyDown);
		}

		if (this.nextButton) {
			this.nextButton.addEventListener('click', this.boundHandleNextClick);
			this.nextButton.addEventListener('keydown', this.boundHandleKeyDown);
		}

		// Add keyboard navigation to the slider container
		this.element.addEventListener('keydown', this.boundHandleSliderKeyDown);

		// Initial navigation state
		this.updateNavigationState(this.slider);
	}

	/**
	 * Initialize optional progress-dot navigation controls.
	 */
	initProgressNavigation() {
		if (!this.parentContainer || !this.slider) return;
		if (this.element.dataset.progressNavigation !== 'true') return;
		if (typeof window.ProgressNavManager !== 'function') return;

		this.progressNavManager = new window.ProgressNavManager(
			this.slider,
			this.parentContainer,
			this.element.dataset.sliderId,
		);
		this.progressNavManager.init((index) => {
			this.stopAutoplay();
			// Use duration:0 so KeenSlider's animator rAF completes the move and
			// updates details.rel before our deferred startAutoplay reads it.
			// startAutoplay is deferred one rAF so it runs after KeenSlider's own
			// animation-step rAF (queued first inside moveToIdx → animator.start).
			// Calling startAutoplay synchronously here means run() reads stale
			// details.rel, animates the wrong dot's fill, and targets the wrong
			// next slide.
			this.slider.moveToIdx(index, false, { duration: 0 });
			this.updateProgressNavigation(index);
			requestAnimationFrame(() => {
				this.startAutoplay(this.slider);
			});
		});
	}

	/**
	 * Update optional progress navigation state.
	 *
	 * @param {number} activeIndex - Active slide index.
	 */
	updateProgressNavigation(activeIndex) {
		if (!this.progressNavManager) return;
		this.progressNavManager.updateActive(activeIndex);
	}

	/**
	 * Apply fade visibility styles to slides when fade transition is enabled.
	 *
	 * @param {Object} slider - The slider instance.
	 */
	updateFadeTransition(slider) {
		if (this.options.transition !== 'fade' || !slider || !slider.track) return;

		const details = slider.track.details;
		if (!details || !Array.isArray(details.slides)) return;

		const slides = Array.from(
			this.element.querySelectorAll('.keen-slider__slide'),
		);

		const activeSlide = slides[details.rel];
		if (activeSlide) {
			this.element.style.minHeight = `${activeSlide.offsetHeight}px`;
		}

		slides.forEach((slide, index) => {
			const isActive = index === details.rel;
			slide.style.opacity = isActive ? '1' : '0';
			slide.style.pointerEvents = isActive ? 'auto' : 'none';
			slide.style.position = 'absolute';
			slide.style.inset = '0';
			slide.style.width = '100%';
		});

		this.element.style.position = 'relative';
	}

	/**
	 * Handle previous button click
	 */
	handlePrevClick() {
		if (!this.slider) return;
		this.slider.prev();
	}

	/**
	 * Handle next button click
	 */
	handleNextClick() {
		if (!this.slider) return;
		this.slider.next();
	}

	/**
	 * Handle keyboard navigation on buttons
	 *
	 * @param {KeyboardEvent} event - The keyboard event
	 */
	handleKeyDown(event) {
		// Allow Enter and Space to trigger button action
		if (event.key === 'Enter' || event.key === ' ') {
			event.preventDefault();
			if (event.target === this.prevButton) {
				this.handlePrevClick();
			} else if (event.target === this.nextButton) {
				this.handleNextClick();
			}
		}
	}

	/**
	 * Handle keyboard navigation on slider container
	 *
	 * @param {KeyboardEvent} event - The keyboard event
	 */
	handleSliderKeyDown(event) {
		switch (event.key) {
			case 'ArrowLeft':
				event.preventDefault();
				if (this.prevButton && !this.prevButton.disabled) {
					this.handlePrevClick();
				}
				break;
			case 'ArrowRight':
				event.preventDefault();
				if (this.nextButton && !this.nextButton.disabled) {
					this.handleNextClick();
				}
				break;
			case 'Home':
				event.preventDefault();
				if (this.slider) {
					this.slider.moveToIdx(0);
				}
				break;
			case 'End':
				event.preventDefault();
				if (!this.slider) break;

				const details = this.slider.track.details;
				if (details && details.slides) {
					this.slider.moveToIdx(details.slides.length - 1);
				}

				break;
		}
	}

	/**
	 * Watch the slider container for size changes and re-evaluate navigation visibility.
	 * ResizeObserver fires per animation frame — no debounce needed.
	 */
	initResizeObserver() {
		if (!this.element || typeof ResizeObserver === 'undefined') return;

		this.resizeObserver = new ResizeObserver(() => {
			// Defer one rAF so KeenSlider finishes updating its track details
			// after the resize before we read them.
			requestAnimationFrame(() => {
				this.updateNavigationVisibility();
				this.updateFadeTransition(this.slider);
			});
		});
		this.resizeObserver.observe(this.element);
	}

	/**
	 * Return true when every slide fits inside the viewport without scrolling.
	 * Uses KeenSlider's normalised track details: distance + size <= 1 means the
	 * last slide's right edge is within the container.
	 */
	allSlidesVisible() {
		if (!this.slider) return false;
		const details = this.slider.track.details;
		if (!details || !Array.isArray(details.slides) || !details.slides.length)
			return false;
		const last = details.slides[details.slides.length - 1];
		return last.distance + last.size <= 1;
	}

	/**
	 * Show or hide the navigation wrapper based on whether all slides are visible.
	 */
	updateNavigationVisibility() {
		if (!this.navigationWrapper) return;
		this.navigationWrapper.hidden = this.allSlidesVisible();
	}

	/**
	 * Update navigation button states based on slider position
	 *
	 * @param {Object} slider - The slider instance
	 */
	updateNavigationState(slider) {
		if (!this.prevButton || !this.nextButton) return;

		try {
			const details = slider.track.details;
			if (!details) return;

			const isAtStart = details.rel === 0;
			const isAtEnd = this.isAtEndPosition(details);
			const loopEnabled = this.options.loop;

			const prevDisabled = !loopEnabled && isAtStart;
			const nextDisabled = !loopEnabled && isAtEnd;

			this.updateButtonState(this.prevButton, prevDisabled, 'previous');
			this.updateButtonState(this.nextButton, nextDisabled, 'next');
		} catch (error) {
			// eslint-disable-next-line no-console
			console.error('KeenSlider navigation state update failed:', error);
		}
	}

	/**
	 * Check if slider is at the end position
	 *
	 * @param {Object} details - The slider details
	 */
	isAtEndPosition(details) {
		if (!details || !details.slides) return true;
		return details.rel >= details.slides.length - 1;
	}

	/**
	 * Update individual button state
	 *
	 * @param {HTMLElement} button    - The button element
	 * @param {boolean}     disabled  - Whether the button is disabled
	 * @param {string}      direction - The direction of the button
	 */
	updateButtonState(button, disabled, direction) {
		if (!button) return;

		// Only update if state has changed to avoid unnecessary DOM operations
		if (button.disabled !== disabled) {
			button.disabled = disabled;
			button.setAttribute('aria-disabled', disabled);
		}

		// Update aria-label only if needed
		const newLabel = disabled
			? `${direction === 'previous' ? 'Previous' : 'Next'} slide (unavailable)`
			: `Go to ${direction} slide`;

		if (button.getAttribute('aria-label') !== newLabel) {
			button.setAttribute('aria-label', newLabel);
		}
	}

	/**
	 * Clean up navigation event listeners
	 */
	destroyNavigation() {
		if (this.prevButton && this.boundHandlePrevClick) {
			this.prevButton.removeEventListener('click', this.boundHandlePrevClick);
			this.prevButton.removeEventListener('keydown', this.boundHandleKeyDown);
			this.prevButton = null;
		}
		if (this.nextButton && this.boundHandleNextClick) {
			this.nextButton.removeEventListener('click', this.boundHandleNextClick);
			this.nextButton.removeEventListener('keydown', this.boundHandleKeyDown);
			this.nextButton = null;
		}
		if (this.element && this.boundHandleSliderKeyDown) {
			this.element.removeEventListener(
				'keydown',
				this.boundHandleSliderKeyDown,
			);
		}

		if (this.progressNavManager) {
			this.progressNavManager.destroy();
			this.progressNavManager = null;
		}

		this.boundHandlePrevClick = null;
		this.boundHandleNextClick = null;
		this.boundHandleKeyDown = null;
		this.boundHandleSliderKeyDown = null;
		this.parentContainer = null;

		if (this.resizeObserver) {
			this.resizeObserver.disconnect();
			this.resizeObserver = null;
		}
		this.navigationWrapper = null;
	}

	/**
	 * Precalculate and cache durations for all slides to avoid performance hits during autoplay
	 */
	precalculateSlideDurations() {
		if (!this.element) return;

		const slideElements = this.element.querySelectorAll('.keen-slider__slide');
		this.durationCache.clear(); // Clear any existing cache

		slideElements.forEach((slideElement, index) => {
			try {
				const slideRect = slideElement.getBoundingClientRect();
				const isLandscape = slideRect.width > slideRect.height;

				let duration = this.animation.duration;

				// Apply multiplier for landscape slides to match visual speed of portrait slides
				if (isLandscape) {
					duration = this.animation.duration * 1.265625; // 486px / 384px = 1.265625
				}

				this.durationCache.set(index, duration);
			} catch (error) {
				// eslint-disable-next-line no-console
				console.error(
					`KeenSlider duration calculation failed for slide ${index}:`,
					error,
				);
				// Fallback to default duration
				this.durationCache.set(index, this.animation.duration);
			}
		});
	}

	/**
	 * Get cached duration for a specific slide index
	 *
	 * @param {number} slideIndex - The index of the slide
	 */
	getCachedDuration(slideIndex) {
		if (this.options.transition === 'fade') {
			return this.animation.duration;
		}

		// If cache is empty (non-autoplay slider), return default duration
		if (this.durationCache.size === 0) {
			return this.animation.duration;
		}
		return this.durationCache.get(slideIndex) || this.animation.duration;
	}

	/**
	 * Invalidate duration cache (useful if slide content changes dynamically)
	 */
	invalidateDurationCache() {
		this.durationCache.clear();
		if (this.slider) {
			this.precalculateSlideDurations();
		}
	}

	/** Stop any pending autoplay timer. */
	stopAutoplay() {
		if (this.autoplayTimer) {
			clearTimeout(this.autoplayTimer);
			this.autoplayTimer = null;
		}

		if (this.progressNavManager) {
			this.progressNavManager.pauseFillTransition();
		}

		if (this.element && this.element.getAttribute('aria-live') === 'off') {
			this.element.setAttribute('aria-live', 'polite');
		}
	}

	/**
	 * Start timer-driven autoplay if enabled.
	 *
	 * @param {Object} slider - The slider instance
	 */
	startAutoplay(slider) {
		if (!this.autoplayEnabled) return;
		this.stopAutoplay();

		if (this.options.transition === 'fade') {
			const runFade = () => {
				if (
					!this.autoplayEnabled ||
					(this.pauseOnHover && this.isHovered) ||
					document.hidden
				) {
					return;
				}

				const details = slider.track.details;
				if (!details) return;

				const currentSlideIndex = details.rel;
				const displayDuration = this.getCachedDuration(currentSlideIndex);

				this.updateProgressNavigation(currentSlideIndex);
				if (this.progressNavManager) {
					this.progressNavManager.animateActiveFill(
						displayDuration,
						currentSlideIndex,
					);
				}

				this.autoplayTimer = setTimeout(() => {
					if (
						!this.autoplayEnabled ||
						(this.pauseOnHover && this.isHovered) ||
						document.hidden
					) {
						return;
					}

					const nextDetails = slider.track.details;
					if (!nextDetails) return;

					let targetAbs = nextDetails.abs + 1;
					if (
						this.options.loop &&
						slider.track &&
						typeof slider.track.idxToDist === 'function' &&
						Array.isArray(nextDetails.slides)
					) {
						let dist = slider.track.idxToDist(targetAbs, true);
						let guard = 0;
						const cycle = nextDetails.slides.length;
						while (dist < 0 && guard < 3) {
							targetAbs += cycle;
							dist = slider.track.idxToDist(targetAbs, true);
							guard++;
						}
					}

					slider.moveToIdx(targetAbs, true, {
						duration: 400,
						easing: (t) => t,
					});

					this.autoplayTimer = setTimeout(runFade, 420);
				}, displayDuration);
			};

			if (this.element && this.element.getAttribute('aria-live') !== 'off') {
				this.element.setAttribute('aria-live', 'off');
			}

			runFade();
			return;
		}

		const run = () => {
			if (
				!this.autoplayEnabled ||
				(this.pauseOnHover && this.isHovered) ||
				document.hidden
			) {
				return;
			}

			const details = slider.track.details;
			if (!details) return;

			const step = this.autoplayOptions.distance;
			let targetAbs = details.abs + step;
			if (
				this.options.loop &&
				slider.track &&
				typeof slider.track.idxToDist === 'function' &&
				Array.isArray(details.slides)
			) {
				let dist = slider.track.idxToDist(targetAbs, true);
				let guard = 0;
				const cycle = details.slides.length;
				while (dist < 0 && guard < 3) {
					targetAbs += cycle;
					dist = slider.track.idxToDist(targetAbs, true);
					guard++;
				}
			}

			// Use cached duration for performance optimization
			const currentSlideIndex = details.rel;
			const animationDuration = this.getCachedDuration(currentSlideIndex);

			const dynamicAnimation = {
				...this.animation,
				duration: animationDuration,
			};

			this.updateProgressNavigation(currentSlideIndex);
			if (this.progressNavManager) {
				this.progressNavManager.animateActiveFill(
					animationDuration,
					currentSlideIndex,
				);
			}

			slider.moveToIdx(targetAbs, true, dynamicAnimation);
			this.autoplayTimer = setTimeout(run, animationDuration);
		};

		if (this.element && this.element.getAttribute('aria-live') !== 'off') {
			this.element.setAttribute('aria-live', 'off');
		}

		run();
	}

	/**
	 * Keep ARIA and focusability in sync with the active slide.
	 *
	 * @param {Object} slider - The slider instance
	 */
	updateA11y(slider) {
		try {
			const details = slider.track.details;
			if (!details) return;

			const slides = Array.from(
				this.element.querySelectorAll('.keen-slider__slide'),
			);
			const activeRel = details.rel;
			const total =
				slides.length || (details.slides ? details.slides.length : 0);

			slides.forEach((el, i) => {
				el.setAttribute('role', 'group');
				if (total) {
					el.setAttribute('aria-roledescription', `slide ${i + 1} of ${total}`);
				} else {
					el.setAttribute('aria-roledescription', 'slide');
				}
				const isActive = i === activeRel;
				el.setAttribute('aria-hidden', 'false');
				el.setAttribute('tabindex', '0');
				if (isActive) {
					el.setAttribute('aria-current', 'true');
				} else {
					el.removeAttribute('aria-current');
				}
			});
		} catch (error) {
			// eslint-disable-next-line no-console
			console.error('KeenSlider accessibility update failed:', error);
		}
	}

	/**
	 * Pause autoplay when any descendant gains focus; resume when focus leaves.
	 */
	bindFocusPause() {
		if (!this.element) return;
		this.element.addEventListener('focusin', () => {
			this.isFocusedWithin = true;
			this.stopAutoplay();
		});
		this.element.addEventListener('focusout', (e) => {
			const stillInside = this.element.contains(e.relatedTarget);
			this.isFocusedWithin = stillInside;
			if (!stillInside && this.autoplayEnabled && this.slider) {
				this.startAutoplay(this.slider);
			}
		});
	}

	/**
	 * Wire hover listeners to pause/resume autoplay when enabled.
	 */
	hoverEvents() {
		if (!this.autoplayEnabled || !this.pauseOnHover) return;

		this.element.addEventListener('mouseenter', () => {
			this.isHovered = true;
			this.stopAutoplay();
			if (
				this.slider &&
				this.slider.animator &&
				typeof this.slider.animator.stop === 'function'
			) {
				this.slider.animator.stop();
			}
		});

		this.element.addEventListener('mouseleave', () => {
			this.isHovered = false;
			if (this.slider) {
				requestAnimationFrame(() => this.startAutoplay(this.slider));
			}
		});
	}
}

document.addEventListener('DOMContentLoaded', function () {
	const sliders = document.querySelectorAll('.keen-slider');
	sliders.forEach((element) => {
		const wrapper = new KeenSliderWrapper(element);
		// Store reference for utility functions
		element._keenSliderWrapper = wrapper;
	});
});

// Expose the wrapper class and add utility methods to the global scope
window.KeenSliderWrapper = KeenSliderWrapper;

// Utility function to invalidate duration cache for a specific slider
window.invalidateSliderDurationCache = function (sliderElement) {
	const wrapper = sliderElement._keenSliderWrapper;
	if (wrapper && typeof wrapper.invalidateDurationCache === 'function') {
		wrapper.invalidateDurationCache();
	}
};
